import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ChatData } from "../types/global.d";
import { UserStatus } from "../constants";
import {
  ensureAdminChatWithNickname,
  getUserChats,
  markMessagesAsRead,
} from "../api/chats";
import { useUserContext } from "./UserContext";

interface ChatContextType {
  chats: ChatData[];
  loading: boolean;
  error: string | null;
  totalUnreadCount: number;
  refreshChats: () => Promise<void>;
  ensureChatWithNickname: (nickname: string) => Promise<ChatData>;
  upsertChat: (chat: ChatData) => void;
  markChatAsRead: (chatId: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const sortChatsByLastMessage = (chats: ChatData[]): ChatData[] => {
  return [...chats].sort((a, b) => {
    const aTime = new Date(a.lastMessage || a.updatedAt || 0).getTime();
    const bTime = new Date(b.lastMessage || b.updatedAt || 0).getTime();
    return bTime - aTime;
  });
};

const hasUnreadForViewer = (
  chat: ChatData,
  currentUserId?: string
): boolean => {
  if ((chat.unreadCount || 0) > 0) {
    return true;
  }

  return (chat.messages || []).some((message) => {
    if (message.read) {
      return false;
    }

    if (currentUserId && message.sender?._id === currentUserId) {
      return false;
    }

    if (message.data?.sentByAdmin === currentUserId) {
      return false;
    }

    return message.sender?.status !== UserStatus.virtual;
  });
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, isLoggedIn } = useUserContext();
  const [chats, setChats] = useState<ChatData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.status === UserStatus.admin;

  const refreshChats = useCallback(async () => {
    if (!isLoggedIn || !isAdmin) {
      setChats([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const list = await getUserChats();
      setChats(sortChatsByLastMessage(list));
    } catch (err: any) {
      setError(err?.message || "Failed to load chats");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isLoggedIn]);

  const upsertChat = useCallback((chat: ChatData) => {
    setChats((previousChats) => {
      const nextChats = previousChats.some((item) => item._id === chat._id)
        ? previousChats.map((item) => (item._id === chat._id ? chat : item))
        : [chat, ...previousChats];

      return sortChatsByLastMessage(nextChats);
    });
  }, []);

  const ensureChatWithNickname = useCallback(
    async (nickname: string) => {
      const existingChat = chats.find((chat) =>
        chat.participants?.some((participant) => participant.nickname === nickname)
      );

      if (existingChat) {
        return existingChat;
      }

      const chat = await ensureAdminChatWithNickname(nickname);
      upsertChat(chat);
      return chat;
    },
    [chats, upsertChat]
  );

  const markChatAsRead = useCallback(
    async (chatId: string) => {
      const chat = chats.find((item) => item._id === chatId);
      if (!chat) {
        await markMessagesAsRead(chatId);
        return;
      }

      if (!hasUnreadForViewer(chat, user?._id)) {
        return;
      }

      await markMessagesAsRead(chatId);
      upsertChat({
        ...chat,
        unreadCount: 0,
        messages: (chat.messages || []).map((message) => ({
          ...message,
          read: true,
        })),
      });
    },
    [chats, upsertChat, user?._id]
  );

  useEffect(() => {
    if (!isLoggedIn || !isAdmin) {
      setChats([]);
      setError(null);
      return;
    }

    refreshChats();
  }, [isAdmin, isLoggedIn, refreshChats]);

  const totalUnreadCount = useMemo(() => {
    return chats.reduce((total, chat) => {
      if (typeof chat.unreadCount === "number") {
        return total + Math.max(chat.unreadCount, 0);
      }

      return (
        total +
        (chat.messages || []).filter((message) => {
          if (message.read) {
            return false;
          }

          if (user?._id && message.sender?._id === user._id) {
            return false;
          }

          if (message.data?.sentByAdmin === user?._id) {
            return false;
          }

          return message.sender?.status !== UserStatus.virtual;
        }).length
      );
    }, 0);
  }, [chats, user?._id]);

  const value = useMemo(
    () => ({
      chats,
      loading,
      error,
      totalUnreadCount,
      refreshChats,
      ensureChatWithNickname,
      upsertChat,
      markChatAsRead,
    }),
    [
      chats,
      loading,
      error,
      totalUnreadCount,
      refreshChats,
      ensureChatWithNickname,
      upsertChat,
      markChatAsRead,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
